import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBlogs } from '../services/blogService';

const Blog = () => {
  const [currentBlogIndex, setCurrentBlogIndex] = useState(0);
  const [blogs, setBlogs] = useState([]);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchDeltaX, setTouchDeltaX] = useState(0);

  const notifyHomeContentUpdated = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('home:content-updated'));
    }
  };

  const toPlainText = (value) =>
    String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const getSnippet = (blog) => {
    const manualPreview = toPlainText(blog?.previewSnippet);
    if (manualPreview) return manualPreview;

    const fromExcerpt = toPlainText(blog?.excerpt);
    if (fromExcerpt) return fromExcerpt;

    const fromIntro = toPlainText(blog?.intro);
    if (fromIntro) return fromIntro;

    return 'Read this blog to explore the full story.';
  };

  const clampSnippet = (text, max = 220) => {
    if (!text || text.length <= max) return text;
    return `${text.slice(0, max).trim()}...`;
  };

  // Load blogs from API
  useEffect(() => {
    let mounted = true;
    getBlogs()
      .then((data) => {
        if (!mounted) return;
        setBlogs(
          (data || []).map((b) => ({
            id: b._id,
            snippet: clampSnippet(getSnippet(b)),
          }))
        );
        notifyHomeContentUpdated();
        setTimeout(notifyHomeContentUpdated, 120);
      })
      .catch((e) => console.error('Failed to load blog titles', e));
    return () => { mounted = false; };
  }, []);

  // Auto-scroll every 22 seconds when blogs are available
  useEffect(() => {
    if (!blogs.length) return;
    const interval = setInterval(() => {
      setCurrentBlogIndex((prevIndex) => (prevIndex + 1) % blogs.length);
    }, 22000);

    return () => clearInterval(interval);
  }, [blogs.length]);

  const handleSwipe = (deltaX) => {
    if (!blogs.length) return;
    if (Math.abs(deltaX) < 45) return;

    if (deltaX < 0) {
      setCurrentBlogIndex((i) => (i + 1) % blogs.length);
    } else {
      setCurrentBlogIndex((i) => (i - 1 + blogs.length) % blogs.length);
    }
  };

  return (
    // RESPONSIVE: Adjusted vertical padding
    <div className="bg-white py-8 md:py-12 relative font-bdogrotesk" style={{ minHeight: '25vh', overflow: 'hidden' }}>
      <div className="w-screen px-2 md:px-4">
        
        {/* Navigation Arrows */}
        {/* RESPONSIVE: Absolute position on desktop, relative flex on mobile to avoid overlap */}
        <div className="flex justify-end md:absolute md:right-4 md:top-4 z-20 gap-2 mb-4 md:mb-0">
          <button
            aria-label="Previous blog"
            onClick={() => {
              if (!blogs.length) return;
              setCurrentBlogIndex((i) => (i - 1 + blogs.length) % blogs.length);
            }}
            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center transition-colors border border-gray-300 rounded-full md:border-none md:rounded-none hover:bg-gray-100 md:hover:bg-transparent"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-5 md:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            aria-label="Next blog"
            onClick={() => {
              if (!blogs.length) return;
              setCurrentBlogIndex((i) => (i + 1) % blogs.length);
            }}
            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center transition-colors border border-gray-300 rounded-full md:border-none md:rounded-none hover:bg-gray-100 md:hover:bg-transparent"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 md:w-5 md:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Blog Display Area */}
        <div
          className="relative overflow-hidden"
          style={{ minHeight: '20vh' }}
          onTouchStart={(e) => {
            const x = e.touches?.[0]?.clientX;
            setTouchStartX(typeof x === 'number' ? x : null);
            setTouchDeltaX(0);
          }}
          onTouchMove={(e) => {
            if (touchStartX === null) return;
            const x = e.touches?.[0]?.clientX;
            if (typeof x !== 'number') return;
            setTouchDeltaX(x - touchStartX);
          }}
          onTouchEnd={() => {
            handleSwipe(touchDeltaX);
            setTouchStartX(null);
            setTouchDeltaX(0);
          }}
        >
          <div 
            className="flex transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${currentBlogIndex * 100}%)` }}
          >
            {blogs.length ? blogs.map((blog) => (
              <div 
                key={blog.id}
                className="flex-shrink-0 w-full"
              >
                {/* RESPONSIVE: Scale font size from 3xl (mobile) to 7xl (desktop) */}
                <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold leading-snug text-black pr-0 md:pr-4 break-words max-w-6xl">
                  {blog.snippet}
                </p>
              </div>
            )) : (
              <div className="flex-shrink-0 w-full">
                <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold leading-snug text-black pr-0 md:pr-4">
                  Loading blog snippet...
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Buttons Row */}
        {/* RESPONSIVE: Stack vertically on mobile, horizontal on desktop */}
        <div className="flex flex-row justify-between items-center mt-8 md:mt-4">
          <Link
            to={blogs.length ? `/blog/?blog=${blogs[currentBlogIndex].id}` : '/blogs'}
            className="home-body-text text-black text-xs md:text-base font-medium tracking-tight hover:underline hover:underline-offset-8 hover:decoration-2 transition-all duration-300"
          >
            Read this blog
          </Link>

          <Link
            to="/blogs"
            className="home-body-text text-black text-xs md:text-base font-medium tracking-tight hover:underline hover:underline-offset-8 hover:decoration-2 transition-all duration-300"
          >
            Read all blogs
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Blog;